import styled, { css } from 'styled-components';
import { Box, BoxProps } from '../box';
import { actionColor, actionLight, colors, rgba } from '../../style';

// Soft, blurred gradient blob used behind the agent builder canvas.
const AGENT_CANVAS_BLOB = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1440' height='1024' viewBox='0 0 1440 1024' fill='none'%3E%3Cg opacity='0.5' filter='url(%23filter0_f_1609_23157)'%3E%3Cpath d='M526.66 121.256C220.531 99.2053 102.313 304.681 -33 450.5C65.9978 411.514 -91.6754 511.47 37.2754 507.5C198.464 502.538 283.775 755 283.775 755C378.275 895.5 594.776 941 1106.28 642C1219.87 778.663 1491.5 911.5 1491.5 911.5C1491.5 911.5 1550 675.5 1556 642C1562 608.5 1562 608.5 1526.28 622.5C1490.55 636.5 1193.11 473.012 1194.28 379C1121.1 499.719 797.167 652.201 607.651 442.752C370.757 180.941 909.322 148.819 526.66 121.256Z' fill='url(%23paint0_linear_1609_23157)'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_f_1609_23157' x='-167' y='-14.3812' width='1859.98' height='1059.88' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape'/%3E%3CfeGaussianBlur stdDeviation='67' result='effect1_foregroundBlur_1609_23157'/%3E%3C/filter%3E%3ClinearGradient id='paint0_linear_1609_23157' x1='819.496' y1='144.996' x2='763.779' y2='828.729' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='white'/%3E%3Cstop offset='0.510586' stop-color='%23D6D6FF'/%3E%3Cstop offset='1' stop-color='%23FFC6AF'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E")`;

export const variants = {
  'white-border': css`
    background: ${colors.white};
    border: 1px solid ${colors.gray300};
  `,
  'white-border-shadow': css`
    background: ${colors.white};
    border: 1px solid ${colors.gray300};
    box-shadow: 0 4px 4px -4px rgba(0, 0, 0, 0.12);
  `,
  'white-border-action': css`
    background: ${colors.white};
    border: 1px solid ${actionColor};
  `,
  'white-border-gray400': css`
    background: ${colors.white};
    border: 1px solid ${colors.gray400};
  `,
  'white-border-attention': css`
    background: ${colors.white};
    border: 1px solid ${colors.attention};
  `,
  'light-violet-border-violet': css`
    background: ${colors.lightViolet};
    border: 1px solid ${colors.violet};
  `,
  shadow: css`
    background: ${colors.white};
    box-shadow: 0 4px 22px rgba(0, 0, 0, 0.07);
  `,
  'shadow-blue-border': css`
    background: ${colors.white};
    box-shadow: 0 4px 22px rgba(0, 0, 0, 0.07);
    border-radius: 8px;
    border: 1px solid ${actionColor};
  `,
  gray900: css`
    background: ${colors.gray900};
  `,
  gray600: css`
    background: ${colors.gray600};
  `,
  gray300: css`
    background: ${colors.gray300};
  `,
  gray100: css`
    background: ${colors.gray100};
  `,
  'surface-tertiary': css`
    background: ${colors.surfaceTertiary};
  `,
  emerald: css`
    background: ${colors.emerald};
  `,
  white: css`
    background: ${colors.white};
  `,
  'action-light': css`
    background: ${actionLight};
  `,
  'border-action-light': css`
    border: 1px solid ${actionLight};
  `,
  'border-transparent': css`
    border: 1px solid ${colors.transparent};
  `,
  action: css`
    background: ${actionColor};
  `,
  'negative-background': css`
    background: ${colors.negativeBackground};
  `,
  'positive-background': css`
    background: ${colors.positiveBackground};
  `,
  'transparent-white': css`
    background: rgba(250, 252, 255, 0.6);
    box-shadow: 0px 12px 12px 0px rgba(0, 0, 0, 0.04);
  `,
  highlight: css`
    background: ${colors.highlight};
  `,
  'shadow-top': css`
    box-shadow: 0 8px 8px 10px rgba(0, 0, 0, 0.12);
    background: ${colors.white};
  `,
  'border-bottom-gray-with-shadow': css`
    border-bottom: 1px solid ${colors.gray300};
    box-shadow: 0 8px 8px -8px rgba(0, 0, 0, 0.12);
  `,
  'border-top-gray-with-shadow': css`
    border-top: 1px solid ${colors.gray300};
    box-shadow: 0 8px 8px 12px rgba(0, 0, 0, 0.12);
  `,
  'border-top-gray300': css`
    background-color: ${colors.gray100};
    border-top: 1px solid ${colors.gray300};
  `,
  'gray100-border-bottom-gray300': css`
    background-color: ${colors.gray100};
    border-bottom: 1px solid ${colors.gray300};
  `,
  'white-border-top-gray300': css`
    background: white;
    border-top: 1px solid ${colors.gray300};
  `,
  'border-bottom-gray300': css`
    border-bottom: 1px solid ${colors.gray300};
  `,
  'border-bottom-gray400': css`
    border-bottom: 1px solid ${colors.gray400};
  `,
  'border-right-gray300': css`
    border-right: 1px solid ${colors.gray300};
  `,
  'border-right-gray400': css`
    border-right: 1px solid ${colors.gray400};
  `,
  'border-sides-gray300': css`
    border-right: 1px solid ${colors.gray300};
    border-left: 1px solid ${colors.gray300};
  `,
  'border-left-gray300': css`
    border-left: 1px solid ${colors.gray300};
  `,
  'border-left-gray400': css`
    border-left: 1px solid ${colors.gray400};
  `,
  'light-sun-gray900': css`
    background: ${colors.lightSun};
    color: ${colors.gray900};
  `,
  warningLight: css`
    background: ${colors.warningLight};
    color: ${colors.gray900};
  `,
  'dark-blue': css`
    background: ${colors.darkBlue};
  `,
  'white-action-border': css`
    background: ${colors.white};
    border: 1px solid ${actionColor};
  `,
  'white-action-border-glow': css`
    background: ${colors.white};
    border: 1px solid ${actionColor};
    box-shadow: 0px 0px 48px -33px rgba(3, 81, 171, 0.63);
    transition: 0.2s;
  `,
  'surface-secondary': css`
    background: ${colors.surfaceSecondary};
  `,
  'surface-secondary-border': css`
    background: ${colors.surfaceSecondary};
    border: 1px solid ${colors.gray300};
  `,
  'surface-secondary-shadow': css`
    background: ${colors.surfaceSecondary};
    box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.1);
  `,
  'light-blue': css`
    background: ${colors.lightBlue};
  `,
  'dashed-gray-100': css`
    background: ${colors.gray100};
    border: 1px dashed ${colors.gray300};
    border-radius: 4px;
  `,
  'action-light-dashed': css`
    background: ${actionLight};
    border: 2px dashed ${actionColor};
  `,
  'white-border-dashed': css`
    background: ${colors.white};
    border: 2px dashed ${colors.gray400};
  `,
  'border-bottom-dashed-gray-300': css`
    border-bottom: 1px dashed ${colors.gray300};
  `,
  'border-top-dashed-gray-300': css`
    border-top: 1px dashed ${colors.gray300};
  `,

  'tab-action-light': css`
    background: ${actionLight};
    position: relative;

    &:after {
      position: absolute;
      height: 100%;
      width: 3px;
      content: '';
      top: 0;
      left: 0;
      background: ${actionColor};
    }
  `,
  'light-pumpkin': css`
    background: ${colors.lightPumpkin};
  `,
  'border-left-light-sun': css`
    border-left: 8px solid ${colors.lightSun};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-light-coral': css`
    border-left: 8px solid ${colors.lightCoral};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-action-light': css`
    border-left: 8px solid ${actionLight};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-light-violet': css`
    border-left: 8px solid ${colors.lightViolet};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'light-teal': css`
    background: ${colors.lightTeal};
  `,
  teal: css`
    background: ${colors.teal};
  `,
  'border-left-violet': css`
    border-left: 4px solid ${colors.violet};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-coral': css`
    border-left: 4px solid ${colors.coral};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-bubbleGum': css`
    border-left: 4px solid ${colors.bubbleGum};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-pumpkin': css`
    border-left: 4px solid ${colors.pumpkin};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  ametystPurple: css`
    background: ${colors.lightAmetystPurple};
    border: 1px solid ${colors.ametystPurple};
    color: ${colors.black};
  `,
  raspberryPink: css`
    background: ${colors.lightRaspberryPink};
    border: 1px solid ${colors.raspberryPink};
    color: ${colors.black};
  `,
  amberGlow: css`
    background: ${colors.lightAmberGlow};
    border: 1px solid ${colors.amberGlow};
    color: ${colors.black};
  `,
  'border-left-ametystPurple': css`
    border-left: 4px solid ${colors.ametystPurple};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-raspberryPink': css`
    border-left: 4px solid ${colors.raspberryPink};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-amberGlow': css`
    border-left: 4px solid ${colors.amberGlow};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-hanBlue': css`
    border-left: 4px solid ${colors.hanBlue};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'border-left-paradiso': css`
    border-left: 4px solid ${colors.paradiso};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
  'coral-background': css`
    background-color: ${colors.coral};
    opacity: 0.8;
  `,
  'pumpkin-background': css`
    background-color: ${colors.pumpkin};
    opacity: 0.8;
  `,
  'carribean-blue-background': css`
    background-color: #14beca;
  `,
  'violet-background': css`
    background: ${colors.lightViolet};
  `,
  'action-subtle-bordered': css`
    background: rgba(79, 70, 229, 0.04);
    border: 1px solid rgba(79, 70, 229, 0.1);
  `,
  'background-gray-transparent': css`
    background: ${colors.gray600}15;
  `,
  'background-amber': css`
    background: ${colors.sun};
  `,
  'background-violet-light': css`
    background: ${colors.lightViolet};
  `,
  'background-teal': css`
    background: ${colors.teal};
  `,
  'background-violet-transparent': css`
    background: ${colors.violet}15;
  `,
  'border-bottom-gray100': css`
    border-bottom: 1px solid ${colors.gray100};
  `,
  'positive-background-border': css`
    background: ${colors.positiveBackground};
    border: 2px solid ${colors.positive};
  `,
  'gray100-border-gray400': css`
    background: ${colors.gray100};
    border: 2px solid ${colors.gray400};
  `,
  'gray50-border-gray300': css`
    background: ${colors.gray50};
    border: 1px solid ${colors.gray300};
  `,
  'gray100-border-gray300': css`
    background: ${colors.gray100};
    border: 1px solid ${colors.gray300};
  `,
  'gray50-border-right-gray300': css`
    background: ${colors.gray50};
    border-right: 1px solid ${colors.gray300};
  `,
  'attention-background-border': css`
    background: ${colors.attentionBackground};
    border: 2px solid ${colors.attention};
  `,
  positive: css`
    background: ${colors.positive};
  `,
  gray400: css`
    background: ${colors.gray400};
  `,
  attention: css`
    background: ${colors.attention};
  `,
  'shadow-transparent': css`
    box-shadow: 0 4px 14px 0px rgba(0, 0, 0, 0.09);
  `,
  'light-primary-bordered': css`
    background: ${actionLight};
    border: 2px solid ${actionColor};
  `,
  'light-primary-bordered-thin': css`
    background: ${props => rgba(actionLight(props), 0.5)};
    border: 1px solid ${actionColor};
  `,
  'graph-paper': css`
    background-color: ${colors.surfaceTertiary};
    background-image:
      linear-gradient(to right, ${colors.gray100} 1px, transparent 1px),
      linear-gradient(to bottom, ${colors.gray100} 1px, transparent 1px);
    background-size: 20px 20px;
  `,
  'agent-canvas-background': css`
    background-color: ${colors.surfaceTertiary};
    background-image:
      linear-gradient(to right, ${rgba(colors.black, 0.04)} 1px, transparent 1px),
      linear-gradient(to bottom, ${rgba(colors.black, 0.04)} 1px, transparent 1px),
      ${AGENT_CANVAS_BLOB};
    background-size:
      20px 20px,
      20px 20px,
      cover;
    background-position: center;
  `,
  'black-border': css`
    border: 2px solid ${colors.black};
  `,
  'action-border': css`
    border: 1px solid ${actionColor};
  `,
};

export interface PanelProps extends BoxProps {
  variant?: keyof typeof variants;
}

const Panel = styled(Box).withConfig({
  shouldForwardProp: prop => !['variant'].includes(prop),
})<PanelProps>`
  ${props => props.variant && variants[props.variant]};
`;

Panel.displayName = 'Panel';

export default Panel;
